/**
 * The hybrid clock — the single time abstraction the scheduler reads.
 *
 * NightBand runs on two clocks at once, and the locked decision is that both matter:
 *
 * 1. **Accelerated game time.** The world runs faster than the player does, so a night's worth
 *    of scheduled transmissions fits in a session. This is what almost all authored content is
 *    scheduled against.
 *
 * 2. **Real wall-clock time.** A few signals are anchored to the actual world — something that
 *    only answers at 03:00 your time, or only on a particular date. The uncanny part of a radio
 *    game is the moment the fiction reaches into the room you are sitting in, so the scheduler
 *    can read real time too.
 *
 * Game time accumulates **only while the clock is running**, not from a wall-clock epoch. That
 * is a deliberate choice: it means ten minutes of play is always two in-game hours, whoever you
 * are and whenever you play. If game time were derived from a fixed real epoch, a player who
 * came back after a week would find the story had run on without them — every scheduled signal
 * silently missed. The elapsed total is persisted with the rest of the save, so the world picks
 * up where the player left it.
 */

/** In-game milliseconds per real millisecond. See DEFAULT_RATIO. */
export type ClockRatio = number;

/**
 * Default acceleration: **1 real minute = 12 in-game minutes**.
 *
 * Which makes one in-game hour five real minutes, and a full in-game night (22:00 → 06:00) about
 * forty minutes — a session. Fast enough that "find it before it moves" has teeth and a player
 * sees several schedule changes per sitting; slow enough that an hour-long authored window
 * doesn't blink past while they are still sweeping the band for it.
 *
 * A content pack overrides this with a single `clock.ratio` value; nothing else needs to change.
 */
export const DEFAULT_RATIO: ClockRatio = 12;

/** The in-game moment a new world starts at: night one, 22:00. */
export const DEFAULT_START_GAME_MS = 22 * 60 * 60 * 1000;

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
export const MS_PER_GAME_DAY = 24 * MS_PER_HOUR;

export interface GameTimeParts {
  /** Day 1 is the first night. */
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** Milliseconds since the start of the current in-game day. */
  msIntoDay: number;
}

/** A single reading of both clocks — what the scheduler resolves against. */
export interface WorldTime {
  /** In-game milliseconds since the world's start moment. */
  gameMs: number;
  game: GameTimeParts;
  /** Real wall-clock epoch milliseconds. */
  realMs: number;
  /** Real local hour (0–23), for real-world-anchored schedules. */
  realHour: number;
  /** Real local weekday, 0 = Sunday. */
  realWeekday: number;
}

/** Convert elapsed real time to elapsed game time. */
export function realToGameMs(realMs: number, ratio: ClockRatio = DEFAULT_RATIO): number {
  return realMs * ratio;
}

/** Convert elapsed game time back to the real time it would take. */
export function gameToRealMs(gameMs: number, ratio: ClockRatio = DEFAULT_RATIO): number {
  return ratio === 0 ? Infinity : gameMs / ratio;
}

/** Break an absolute in-game timestamp into day/hour/minute/second. */
export function gameTimeParts(gameMs: number): GameTimeParts {
  // Floor rather than truncate so a negative timestamp (a schedule window that opens "before"
  // the world started) still lands on a sane day boundary instead of mirroring around zero.
  const day = Math.floor(gameMs / MS_PER_GAME_DAY) + 1;
  const msIntoDay = gameMs - Math.floor(gameMs / MS_PER_GAME_DAY) * MS_PER_GAME_DAY;
  return {
    day,
    hour: Math.floor(msIntoDay / MS_PER_HOUR),
    minute: Math.floor((msIntoDay % MS_PER_HOUR) / MS_PER_MINUTE),
    second: Math.floor((msIntoDay % MS_PER_MINUTE) / MS_PER_SECOND),
    msIntoDay,
  };
}

/** `HH:MM` in game time — the receiver's clock readout. */
export function formatGameClock(parts: GameTimeParts): string {
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
}

/**
 * Is `hour` inside the window `[from, to)`? Windows wrap midnight, which they nearly always do
 * in this game — `{ from: 22, to: 6 }` is a night, not an empty set.
 */
export function hourInWindow(hour: number, from: number, to: number): boolean {
  if (from === to) return true; // a full turn of the clock
  return from < to ? hour >= from && hour < to : hour >= from || hour < to;
}

/** Same question for absolute in-game milliseconds against an in-day window. */
export function gameMsInDailyWindow(gameMs: number, fromHour: number, toHour: number): boolean {
  return hourInWindow(gameTimeParts(gameMs).hour, fromHour, toHour);
}

export interface ClockOptions {
  ratio?: ClockRatio;
  /** In-game timestamp a fresh world starts at. */
  startGameMs?: number;
  /** Game time already accumulated in previous sessions. */
  elapsedGameMs?: number;
  /** Injectable clocks — the tests drive these instead of waiting for real seconds to pass. */
  nowMs?: () => number;
  localTimeOf?: (realMs: number) => { hour: number; weekday: number };
}

/**
 * The clock itself. Start it when play begins, stop it when the receiver is put down; read
 * `time()` whenever the scheduler needs to know what is on the air.
 */
export class WorldClock {
  readonly ratio: ClockRatio;
  private readonly startGameMs: number;
  private readonly nowMs: () => number;
  private readonly localTimeOf: (realMs: number) => { hour: number; weekday: number };

  private accumulatedGameMs: number;
  /** Real timestamp of the last accumulation point, or null while stopped. */
  private runningSince: number | null = null;

  constructor(options: ClockOptions = {}) {
    this.ratio = options.ratio ?? DEFAULT_RATIO;
    this.startGameMs = options.startGameMs ?? DEFAULT_START_GAME_MS;
    this.accumulatedGameMs = options.elapsedGameMs ?? 0;
    this.nowMs = options.nowMs ?? (() => Date.now());
    this.localTimeOf =
      options.localTimeOf ??
      ((realMs: number) => {
        const date = new Date(realMs);
        return { hour: date.getHours(), weekday: date.getDay() };
      });
  }

  get isRunning(): boolean {
    return this.runningSince !== null;
  }

  start(): void {
    if (this.runningSince !== null) return;
    this.runningSince = this.nowMs();
  }

  stop(): void {
    if (this.runningSince === null) return;
    this.accumulatedGameMs = this.elapsedGameMs();
    this.runningSince = null;
  }

  /** Total game time this world has lived through. Persist this; pass it back in on load. */
  elapsedGameMs(): number {
    if (this.runningSince === null) return this.accumulatedGameMs;
    return this.accumulatedGameMs + realToGameMs(this.nowMs() - this.runningSince, this.ratio);
  }

  /** Both clocks, read at the same instant. */
  time(): WorldTime {
    const realMs = this.nowMs();
    const gameMs = this.startGameMs + this.elapsedGameMs();
    const local = this.localTimeOf(realMs);
    return {
      gameMs,
      game: gameTimeParts(gameMs),
      realMs,
      realHour: local.hour,
      realWeekday: local.weekday,
    };
  }

  /**
   * Jump the world forward. Used by authored content ("the night passes") and by the dev tools;
   * never by the player directly.
   */
  advanceGameMs(deltaGameMs: number): void {
    this.accumulatedGameMs = this.elapsedGameMs() + deltaGameMs;
    if (this.runningSince !== null) this.runningSince = this.nowMs();
  }
}
